import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import {
  ShippingCompany,
  SpecificCarrierPriorityShipmentsInputDTO,
  SubscriberInfo
} from 'src/app/admin-api';
import { IntegrationControllerService } from 'src/app/admin-api/api/integrationController.service';
import { ShippingCompanyControllerService } from 'src/app/admin-api/api/shippingCompanyController.service';
import { SubscriberControllerService } from 'src/app/admin-api/api/subscriberController.service';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-specific-carrier-priority-form',
  templateUrl: './specific-carrier-priority-form.component.html',
  styleUrl: './specific-carrier-priority-form.component.scss'
})
export class SpecificCarrierPriorityFormComponent implements OnInit {
  public ref = inject(DynamicDialogRef);
  private integrationService = inject(IntegrationControllerService);
  private subscriberService = inject(SubscriberControllerService);
  private shippingCompanyService = inject(ShippingCompanyControllerService);

  searchForm = new FormGroup({
    personId: new FormControl<number | null>(null),
    subscriberId: new FormControl<number | null>(null),
    email: new FormControl<string | null>(null),
    boxId: new FormControl<string | null>(null)
  });

  form = new FormGroup({
    personId: new FormControl<number | null>(null, Validators.required),
    shippingCompanyId: new FormControl<number | null>(
      null,
      Validators.required
    ),
    reason: new FormControl<string>('', [Validators.minLength(6)])
  });

  subscribers: SubscriberInfo[] = [];
  selectedSubscriber: SubscriberInfo | null = null;
  shippingCompanies: ShippingCompany[] = [];
  searched = false;

  async ngOnInit(): Promise<void> {
    try {
      LoaderService.showLoader();
      this.shippingCompanies = await lastValueFrom(
        this.shippingCompanyService
          .getShippingCompanies()
          .pipe(map((data) => data.result ?? []))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async search(): Promise<void> {
    const { personId, subscriberId, email, boxId } = this.searchForm.value;
    if (!personId && !subscriberId && !email && !boxId) {
      return;
    }
    try {
      LoaderService.showLoader();
      this.subscribers = await lastValueFrom(
        this.subscriberService
          .search(
            personId ?? undefined,
            subscriberId ?? undefined,
            email ?? undefined,
            boxId ?? undefined
          )
          .pipe(map((data) => data.result ?? []))
      );
      this.searched = true;
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  selectSubscriber(subscriber: SubscriberInfo): void {
    this.selectedSubscriber = subscriber;
    this.form.patchValue({ personId: subscriber.personId ?? null });
  }

  clearSubscriber(): void {
    this.selectedSubscriber = null;
    this.form.patchValue({ personId: null });
  }

  async submit(): Promise<void> {
    if (!this.form.valid) return;
    try {
      LoaderService.showLoader();
      const body: SpecificCarrierPriorityShipmentsInputDTO = {
        personId: this.form.value.personId as number,
        shippingCompanyId: this.form.value.shippingCompanyId as number,
        reason: this.form.value.reason as string
      };
      const result = await lastValueFrom(
        this.integrationService.create(body).pipe(map((data) => data.result))
      );
      this.ref.close(result);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }
}
